import React, { useState, useEffect } from 'react';
import { collection, addDoc, onSnapshot, doc, updateDoc } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import './AddCategoryDialog.css';

function AddCategoryDialog({ isOpen, onClose, onSuccess, editCategory = null }) {
  const [categoryType, setCategoryType] = useState('expense'); // 'income' or 'expense'
  const [name, setName] = useState('');
  const [includeInBudget, setIncludeInBudget] = useState(true);
  const [group, setGroup] = useState('');
  const [ledger, setLedger] = useState('');
  const [ledgerOptions, setLedgerOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const user = auth.currentUser;

  const isEditMode = editCategory !== null;

  const incomeGroups = ['Salaries', 'Sundry Income'];
  const expenseGroups = ['Financial', 'Utilities', 'Food', 'Leisure', 'Other', 'Personal', 'Housing', 'Transport', 'Business'];

  const currentGroups = categoryType === 'income' ? incomeGroups : expenseGroups;

  // Load available ledger codes from Chart of Accounts
  useEffect(() => {
    if (!user) return;
    
    console.log('Loading ledger codes for user:', user.uid);
    console.log('Category type:', categoryType);
    
    const chartofAccRef = collection(db, 'users', user.uid, 'ChartofAcc');
    const unsubscribe = onSnapshot(chartofAccRef, (snapshot) => {
      console.log('ChartofAcc collection size:', snapshot.size);
      
      const allLedgers = [];
      
      snapshot.forEach(doc => {
        console.log('Found document:', doc.id);
        const data = doc.data();
        console.log('Document data:', data);
        
        // Loop through all fields in the document
        Object.entries(data).forEach(([ledgerCode, isUsed]) => {
          console.log(`Checking ${ledgerCode}: ${isUsed} (type: ${typeof isUsed})`);
          
          // Only include if not used (false) and matches category type
          if (isUsed === false) {
            if (categoryType === 'income') {
              // Income ledgers contain "INC" or start with 4
              if (ledgerCode.includes('INC') || ledgerCode.startsWith('4')) {
                console.log(`Adding income ledger: ${ledgerCode}`);
                allLedgers.push(ledgerCode);
              }
            } else {
              // Expense ledgers contain "EXP"
              if (ledgerCode.includes('EXP')) {
                console.log(`Adding expense ledger: ${ledgerCode}`);
                allLedgers.push(ledgerCode);
              }
            }
          }
        });
      });
      
      // Sort ledger codes
      allLedgers.sort();
      console.log('Final ledger options:', allLedgers);
      setLedgerOptions(allLedgers);
    });
    
    return unsubscribe;
  }, [user, categoryType]);

  // Reset group when category type changes
  useEffect(() => {
    setGroup('');
  }, [categoryType]);

  // Populate form when editing
  useEffect(() => {
    if (editCategory) {
      setName(editCategory.name || '');
      setIncludeInBudget(editCategory.includeInBudget ?? true);
      setGroup(editCategory.group || '');
      setLedger(editCategory.Ledger || '');
      setCategoryType(editCategory.categoryType || 'expense');
    } else {
      // Reset form when not editing
      setName('');
      setIncludeInBudget(true);
      setGroup('');
      setLedger('');
      setCategoryType('expense');
    }
  }, [editCategory]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!name.trim()) {
      alert('Please enter a category name');
      return;
    }
    
    if (!group) {
      alert('Please select a group');
      return;
    }
    
    if (!ledger) {
      alert('Please select a ledger code');
      return;
    }
    
    setLoading(true);
    
    try {
      const categoryData = {
        name: name.trim(),
        includeInBudget: includeInBudget,
        group: group,
        Ledger: ledger
      };
      
      if (isEditMode) {
        // Update existing category
        const collectionName = editCategory.categoryType === 'income' ? 'income_categories' : 'expense_categories';
        const docRef = doc(db, 'users', user.uid, collectionName, editCategory.id);
        await updateDoc(docRef, categoryData);
        alert('Category updated successfully!');
      } else {
        // Add new category
        if (categoryType === 'income') {
          await addDoc(collection(db, 'users', user.uid, 'income_categories'), categoryData);
        } else {
          await addDoc(collection(db, 'users', user.uid, 'expense_categories'), categoryData);
        }
        alert('Category added successfully!');
      }
      
      // Reset form
      setName('');
      setGroup('');
      setLedger('');
      setIncludeInBudget(true);
      
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="dialog-overlay" onClick={onClose}>
      <div className="dialog-content" onClick={(e) => e.stopPropagation()}>
        <div className="dialog-header">
          <h3>{isEditMode ? 'Edit Category' : 'Add Category'}</h3>
          <button className="btn-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="dialog-body">
            
            {/* Category Type */}
            <div className="form-field">
              <label>Category Type *</label>
              <div className="radio-group">
                <label className="radio-option">
                  <input
                    type="radio"
                    name="categoryType"
                    value="income"
                    checked={categoryType === 'income'}
                    onChange={(e) => setCategoryType(e.target.value)}
                    disabled={isEditMode}
                  />
                  <span>Income</span>
                </label>
                <label className="radio-option">
                  <input
                    type="radio"
                    name="categoryType"
                    value="expense"
                    checked={categoryType === 'expense'}
                    onChange={(e) => setCategoryType(e.target.value)}
                    disabled={isEditMode}
                  />
                  <span>Expense</span>
                </label>
              </div>
            </div>
            
            {/* Name */}
            <div className="form-field">
              <label>Category Name *</label>
              <input
                type="text"
                className="form-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter category name"
                required
              />
            </div>
            
            {/* Group */}
            <div className="form-field">
              <label>Group *</label>
              <select
                className="form-select"
                value={group}
                onChange={(e) => setGroup(e.target.value)}
                required
              >
                <option value="">Select a group</option>
                {currentGroups.map(g => (
                  <option key={g} value={g}>{g}</option>
                ))}
              </select>
            </div>
            
            {/* Ledger */}
            <div className="form-field">
              <label>Ledger Code *</label>
              <select
                className="form-select"
                value={ledger}
                onChange={(e) => setLedger(e.target.value)}
                required
              >
                <option value="">Select a ledger code</option>
                {ledgerOptions.map(code => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Include in Budget */}
            <div className="form-field">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeInBudget}
                  onChange={(e) => setIncludeInBudget(e.target.checked)}
                />
                <span>Include in Budget</span>
              </label>
            </div>
            
          </div>
          
          <div className="dialog-footer">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Category' : 'Add Category')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AddCategoryDialog;